import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { StageExecutionEditDialog } from '@/features/pipelines/components/StageExecutionEditDialog';
import {
  useDeletePipelineStageExecution,
  useUpdatePipelineStageExecution,
} from '@/features/pipelines/hooks/pipelines';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import {
  buildStageDetailPath,
  buildStageExecutionDetailPath,
  buildSyntheticStageId,
} from '@/features/pipelines/utils/stagePaths';
import { useProjectFilter } from '@/features/projects';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName, resolveTagColor } from '@/features/shared/tags';
import {
  resolveCiStatusLabelKey,
  resolveStageExecutionDisplayStatus,
} from '@/features/shared/utils/ciStatus';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getToastMessage } from '@/shared/copy/toasts';
import { useTagOptions } from '@/shared/hooks';
import { Box, formatDuration, formatStatusLabel } from '@/ui';
import { buildLocalQueryResult } from '@/ui/composites/grid/localGridUtils';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import {
  resolveStatusChipTone,
  StagesIcon,
  type GridColDef,
  type GridColumnVisibilityModel,
} from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { PipelineStageExecutionListItemDto, QueryResult } from '@flowqa/shared';

export type StageExecutionRow = PipelineStageExecutionListItemDto;

type StageExecutionsTableProps = {
  rows?: StageExecutionRow[];
  noRowsText?: string;
  queryKey: QueryKey;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<StageExecutionRow>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  search?: string;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
};

type StageExecutionColumnsOptions = {
  tagColorByName?: Partial<Record<string, TagColor>>;
  includeActionsColumn?: boolean;
  onEditRow?: (row: StageExecutionRow) => void;
  onDeleteRow?: (row: StageExecutionRow) => void;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
};

const columnFlex = {
  stage: 0.95,
  run: 0.45,
  status: 0.7,
  jobs: 0.55,
  actions: 0.1,
};

const mapStageExecutionJobSummaryItems = (row: StageExecutionRow): SummaryTooltipItem[] =>
  (row.jobSummaries ?? []).map((job) => {
    const runNumber = resolveBuildRunNumber(job.buildKey ?? null);

    return {
      key: job.id,
      detailLabel: row.stageName,
      executionLabel: runNumber === null ? null : `#${runNumber}`,
      href: buildStageExecutionDetailPath({
        stageExecutionId: row.id,
        pipelineId: row.pipelineId,
        stageName: row.stageName,
      }),
      executionHref: buildJobRunDetailPath({
        jobRunId: job.id,
        pipelineId: row.pipelineId,
        stageName: row.stageName,
        jobName: job.jobName,
      }),
      runNumber,
      status: job.status,
      timestamp: job.timestamp,
    };
  });

const buildStageExecutionColumns = ({
  tagColorByName = {},
  includeActionsColumn = false,
  onEditRow,
  onDeleteRow,
  editRowPendingId = null,
  deleteRowPendingId = null,
}: StageExecutionColumnsOptions): Array<GridColDef<StageExecutionRow>> => {
  const stageColumn: GridColDef<StageExecutionRow> = {
    field: 'stageName',
    headerName: getDetailLabel('stage'),
    flex: columnFlex.stage,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-name-cell',
    renderCell: (params): ReactElement => {
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);

      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label: params.row.stageName,
              href: buildStageDetailPath(
                buildSyntheticStageId(params.row.pipelineId, params.row.stageName),
              ),
            }}
            secondary={
              params.row.pipelineName
                ? {
                    label: params.row.pipelineName,
                    href: pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines',
                  }
                : null
            }
            tags={(params.row.tags ?? []).map((tag) => ({
              label: tag,
              tone: resolveTagColor(tag, tagColorByName),
            }))}
          />
        </TableCellContainer>
      );
    },
  };

  const runColumn: GridColDef<StageExecutionRow> = {
    field: 'buildKey',
    headerName: getDetailLabel('run'),
    flex: columnFlex.run,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-run-link-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const runNumber = resolveBuildRunNumber(params.row.buildKey ?? null);
      const href = buildStageExecutionDetailPath({
        stageExecutionId: params.row.id,
        pipelineId: params.row.pipelineId,
        stageName: params.row.stageName,
      });

      return (
        <TableCellContainer gap="none">
          <RunLinkCell
            label={runNumber !== null ? `#${runNumber}` : 'View'}
            href={href}
            latest={params.row.isLatest === true}
          />
        </TableCellContainer>
      );
    },
  };

  const statusColumn: GridColDef<StageExecutionRow> = {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnFlex.status,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const status = resolveStageExecutionDisplayStatus(params.row) ?? params.row.status;
      const statusTone = resolveStatusChipTone(status);
      const StatusIcon = getBuildStatusIconComponent(status);
      const durationLabel = formatDuration(params.row.durationMs ?? null);
      const timestamp = params.row.finishedAt ?? params.row.startedAt ?? params.row.lastUpdatedAt;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runHref = buildStageExecutionDetailPath({
        stageExecutionId: params.row.id,
        pipelineId: params.row.pipelineId,
        stageName: params.row.stageName,
      });

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={formatStatusLabel(resolveCiStatusLabelKey(status))}
            statusHref={runHref}
            chipTone={statusTone}
            chipIcon={<Box component={StatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const jobsColumn: GridColDef<StageExecutionRow> = {
    field: 'jobStatusCounts',
    headerName: 'Jobs',
    flex: columnFlex.jobs,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => (
      <TableCellContainer gap="none">
        <TableBuildStatusBreakdownCell
          total={params.row.jobTotal ?? 0}
          counts={params.row.jobStatusCounts}
          summaryItems={mapStageExecutionJobSummaryItems(params.row)}
          totalIcon={StagesIcon}
          showDateHeaders={false}
        />
      </TableCellContainer>
    ),
  };

  const actionsColumn: GridColDef<StageExecutionRow> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params): ReactElement => (
      <TableActionsCell
        rowName={params.row.stageName}
        editActionLabel={getEntityActionLabel('edit', 'Stage Execution')}
        deleteActionLabel={getEntityActionLabel('delete', 'Stage Execution')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={editRowPendingId === params.row.id || deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    stageColumn,
    runColumn,
    statusColumn,
    jobsColumn,
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};

const filterStageExecutionRowsBySearch = (
  rows: StageExecutionRow[],
  search: string | undefined,
): StageExecutionRow[] => {
  const normalizedSearch = search?.trim().toLowerCase();
  if (!normalizedSearch) return rows;
  return rows.filter((row) =>
    [
      row.pipelineName ?? '',
      row.stageName,
      row.status,
      row.buildKey ?? '',
      ...(row.tags ?? []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch)),
  );
};

const StageExecutionsTableComponent = ({
  rows = [],
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'stageExecution' }),
  queryKey,
  queryFn,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize = 5,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: StageExecutionsTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const updateStageExecution = useUpdatePipelineStageExecution();
  const deleteStageExecution = useDeletePipelineStageExecution();
  const [editTarget, setEditTarget] = useState<StageExecutionRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StageExecutionRow | null>(null);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns = useMemo(
    () =>
      buildStageExecutionColumns({
        tagColorByName,
        includeActionsColumn: true,
        onEditRow: (row) => {
          setEditTarget(row);
        },
        onDeleteRow: (row) => {
          setDeleteTarget(row);
        },
        editRowPendingId: updateStageExecution.isPending ? (editTarget?.id ?? null) : null,
        deleteRowPendingId: deleteStageExecution.isPending ? (deleteTarget?.id ?? null) : null,
      }),
    [
      deleteStageExecution.isPending,
      deleteTarget?.id,
      updateStageExecution.isPending,
      editTarget?.id,
      tagColorByName,
    ],
  );
  const togglableColumnFields = useMemo(() => columns.map((column) => column.field), [columns]);

  const localQueryFn = useMemo(
    () =>
      queryFn ??
      (async (input: GridQueryInput): Promise<QueryResult<StageExecutionRow>> => {
        const filteredRows = filterStageExecutionRowsBySearch(rows, search);
        return await Promise.resolve(buildLocalQueryResult(filteredRows, input));
      }),
    [queryFn, rows, search],
  );

  const gridProps = buildTableGridProps<StageExecutionRow>({
    queryFn: localQueryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    noRowsText,
  });

  return (
    <>
      <ServerDataTable<StageExecutionRow>
        queryPath="stage-runs/query"
        queryFn={gridProps.queryFn}
        queryKey={gridProps.queryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={Boolean(session?.userId)}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={gridProps.pageSizeOptions}
        initialPageSize={gridProps.initialPageSize}
        noRowsText={gridProps.noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        autoHeight
        showToolbar={false}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        columnVisibilityModel={columnVisibilityModel}
        sx={tableSx}
      />
      <StageExecutionEditDialog
        open={editTarget !== null}
        execution={editTarget}
        loading={updateStageExecution.isPending}
        onClose={() => {
          if (updateStageExecution.isPending) {
            return;
          }
          setEditTarget(null);
        }}
        onSave={async ({ status, startedAt, finishedAt, durationMs }) => {
          if (!editTarget) {
            return;
          }

          await updateStageExecution.mutateAsync({
            id: editTarget.id,
            projectId: editTarget.projectId,
            status,
            startedAt,
            finishedAt,
            durationMs,
          });
          notify({
            message: 'Stage execution updated',
            tone: 'green',
          });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'stageExecution' })}
        description="This stage execution will be removed from the stage execution history view."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'stageExecution',
        })}
        confirmColor="red"
        loading={deleteStageExecution.isPending}
        onClose={() => {
          if (deleteStageExecution.isPending) {
            return;
          }
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) {
            return;
          }

          deleteStageExecution.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'stage execution',
                    name: deleteTarget.stageName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
            },
          );
        }}
      />
    </>
  );
};

export const StageExecutionsTable = memo(StageExecutionsTableComponent);
